import {useEffect, useRef} from 'react'
import {useIntl} from 'react-intl'
import {
    useCustomMutation,
    useCustomerType,
    useShopperBasketsMutation
} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {useOptionalBrand} from '../contexts/brand-context'
import {useCustomToast} from '../contexts/custom-toast-context'
import {preferenceToBool, useSitePreferences} from './use-site-preferences'
import useGscAttribution from './use-gsc-attribution'

/**
 * Handoff tokens currently being resolved, module-scoped rather than per-hook-instance
 * so that React 18 StrictMode's development double-mount (two `useGscHandoff()`
 * instances mounting back to back) can never trigger the same token twice.
 * @type {Set<string>}
 */
const inFlightTokens = new Set()

/**
 * Reads the `gscToken` query parameter off the current URL. Safe to call during
 * SSR, where it always returns `null` since there is no `window`.
 * @returns {string|null} The token, or `null` when absent or off the server.
 */
const getTokenFromLocation = () => {
    if (typeof window === 'undefined') return null
    return new URL(window.location.href).searchParams.get('gscToken')
}

/**
 * Removes `gscToken` from the current URL via a history replace, leaving every
 * other query parameter, path segment and hash untouched, so a page refresh never
 * re-triggers the handoff. A no-op off the server or when the token is already
 * gone. Never throws: a browser that rejects the history mutation (for example, a
 * sandboxed iframe) must not block the rest of the handoff.
 * @returns {void}
 */
const stripTokenFromLocation = () => {
    if (typeof window === 'undefined') return
    try {
        const url = new URL(window.location.href)
        if (!url.searchParams.has('gscToken')) return
        url.searchParams.delete('gscToken')
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
    } catch (error) {
        logFailure('token URL cleanup failed', error)
    }
}

/**
 * Builds the brand-scoped site preference id that gates GSC on the web channel,
 * e.g. `gscEnabledGarageWeb`. Any brand other than `'dynamite'` resolves to Garage,
 * matching the hook's own default brand.
 * @param {string} brandId - The current storefront brand, e.g. `'garage'` or `'dynamite'`.
 * @returns {string} The site preference id to read via {@link useSitePreferences}.
 */
const getPreferenceId = (brandId) =>
    `gscEnabled${brandId === 'dynamite' ? 'Dynamite' : 'Garage'}Web`

/**
 * Unwraps a `useCustomMutation`/`useShopperBasketsMutation` result to the plain
 * response body, regardless of whether the SDK returned `{data: ...}` or the body
 * directly.
 * @param {{data?: Object}|Object|null|undefined} response
 * @returns {Object|null|undefined}
 */
const unwrapResponse = (response) => response?.data || response

/**
 * Whether a GSC handoff item has a usable SKU and a positive integer quantity.
 * The resolve endpoint validates this server-side, so a malformed item should
 * never actually reach the client — this only guards against sending
 * `productId: undefined` to `addItemToBasket` if that contract is ever violated.
 * @param {{sku?: unknown, qty?: unknown}|null|undefined} item
 * @returns {boolean}
 */
const isValidHandoffItem = (item) =>
    typeof item?.sku === 'string' && item.sku.length > 0 && Number.isInteger(item?.qty) && item.qty > 0

/**
 * Sums product line quantities by product id, so a bulk add whose outcome is
 * unknown can be checked against the basket afterwards.
 * @param {Object|null|undefined} basket - A SCAPI basket.
 * @returns {Object<string, number>} Quantity per product id.
 */
const quantitiesByProduct = (basket) =>
    (basket?.productItems || []).reduce((result, item) => {
        if (item?.productId) {
            result[item.productId] = (result[item.productId] || 0) + (Number(item.quantity) || 0)
        }
        return result
    }, {})

/**
 * Whether a rejected SCAPI call definitely did not change the basket: a 4xx means
 * the request was refused (a basket hook returning an error rolls back its
 * transaction). Anything else (no response, a timeout, a 5xx) leaves the outcome
 * unknown.
 * @param {(Error & {response?: Response})|null|undefined} error
 * @returns {boolean}
 */
const isDefinitiveRejection = (error) => {
    const status = error?.response?.status
    return Number.isInteger(status) && status >= 400 && status < 500
}

/**
 * Logs a GSC handoff failure under the shared `gsc` namespace. Never
 * throws, so it is always safe to call from a `catch` block.
 * @param {string} message - Short description of what failed.
 * @param {Error|null|undefined} error - The error that was caught.
 * @returns {void}
 */
const logFailure = (message, error) => {
    logger.error(`GSC cart handoff: ${message}`, {
        namespace: 'gsc',
        additionalProperties: {error: error?.message, stack: error?.stack}
    })
}

/**
 * Logs a GSC handoff milestone under the shared `gsc` namespace, at info level.
 * Used to trace the happy path end-to-end (resolve succeeded, stamp attempted,
 * final basket state) since {@link logFailure} alone only shows what broke, not
 * what should have worked. Never throws.
 * @param {string} message - Short description of the milestone.
 * @param {Object} [properties] - Extra non-PII context (basket id, booleans, counts).
 * @returns {void}
 */
const logInfo = (message, properties) => {
    logger.info(`GSC cart handoff: ${message}`, {
        namespace: 'gsc',
        additionalProperties: properties
    })
}

/**
 * Extracts a human-readable failure reason from a rejected `addItemToBasket` call,
 * so the reason recorded on the basket (and later the order note) is the actual
 * SCAPI Problem/JSON detail rather than a useless generic string.
 *
 * commerce-sdk-isomorphic's thrown error only sets `.message` to
 * `"{status} {statusText}"` (e.g. `"400 Bad Request"`) — the real detail (`title`,
 * `detail`) lives in the raw `Response` body at `.response`, which has to be read
 * asynchronously. Falls back to `.message` when the body can't be read or parsed
 * (network error, non-JSON body), and never throws.
 *
 * @param {(Error & {response?: Response})|null|undefined} error
 * @returns {Promise<string>}
 */
const extractFailureReason = async (error) => {
    try {
        const body = await error?.response?.json()
        const reason = body?.detail || body?.title
        if (reason) return reason
    } catch (parseError) {
        // Response body wasn't JSON or couldn't be read — fall through to `.message`.
    }
    return error?.message || 'Unknown error'
}

/**
 * Resolves a GSC microsite cart handoff on the cart page and stamps the resulting
 * creator attribution onto the shopper's basket.
 *
 * Reads `gscToken` from the URL once per mount (see {@link getTokenFromLocation}),
 * and does nothing at all — no network call, no basket mutation — when there is no
 * token, or when the current brand's web feature flag is off. When a token is
 * present, it resolves the token exactly once (deduplicated across React 18
 * StrictMode's development double-mount via {@link inFlightTokens}, and across a
 * page refresh via the basket's own `c_gscHandoffTokenId`), appends any returned
 * items to the existing basket with a bulk-add-then-per-item-retry fallback, and
 * stamps the basket with the resolved attribution — including when the response
 * carries no items at all, since an attribution-only handoff is a real, valid
 * outcome. The token is stripped from the URL as soon as the handoff starts (and
 * again when it finishes, on every outcome including failure), and the basket is
 * stamped with the token before any item is added, so a refresh or a second tab
 * can never re-run the flow and add the items twice. A handoff on an ESW
 * international locale is ignored entirely.
 *
 * Every failure path — an unresolvable token, a GSC-side error, a rejected item
 * add, a basket-creation failure — degrades to "leave the cart usable and log the
 * error"; nothing here can block cart rendering or checkout.
 *
 * @returns {void} This hook has no return value; it only produces side effects
 *   (basket mutations, a URL history replace, and an optional non-blocking toast).
 */
const useGscHandoff = () => {
    const tokenRef = useRef(getTokenFromLocation())
    const gscToken = tokenRef.current
    const bodyRef = useRef(null)
    const isMountedRef = useRef(false)
    const {formatMessage} = useIntl()
    const {showCustomToast} = useCustomToast()
    // The UK (en-GB) build has no BrandProvider, and useBrand() throws without one, so
    // read the context optionally and fall back to Garage, the only brand UK serves.
    const brandContext = useOptionalBrand()
    const brandId = (brandContext?.brandId || 'garage').toLowerCase()
    const preferenceId = getPreferenceId(brandId)
    const {site, locale} = useMultiSite()
    // GSC is in scope for US, CA and UK domestic only. ESW international locales on the
    // UK site are out of scope, so a creator link opened there is ignored entirely.
    const isIntlLocale = locale?.isSupportedByESW === true
    const {data: preferences, isLoading: preferencesLoading} = useSitePreferences([preferenceId], {
        enabled: Boolean(gscToken) && !isIntlLocale
    })
    const enabled = preferenceToBool(preferences?.[preferenceId])
    const {customerType} = useCustomerType()
    const {data: basket, refetch, isLoading: basketLoading} = useCurrentBasket()
    const {mutateAsync: addItemToBasket} = useShopperBasketsMutation('addItemToBasket')
    const {mutateAsync: createBasket} = useShopperBasketsMutation('createBasket')
    const {stampBasket, restampBasket} = useGscAttribution()

    const resolveHandoff = useCustomMutation({
        options: {
            method: 'POST',
            // schema.yaml only declares `siteId` as a query parameter. The platform
            // rejects any undeclared query parameter with a 400, so don't add one
            // (e.g. locale) without declaring it in the schema first.
            parameters: {siteId: site?.id},
            get body() {
                return bodyRef.current
            },
            customApiPathParameters: {
                apiVersion: 'v1',
                endpointPath: '/resolve',
                apiName: 'gsc-handoff'
            }
        }
    })

    useEffect(() => {
        isMountedRef.current = true
        return () => {
            isMountedRef.current = false
        }
    }, [])

    useEffect(() => {
        if (!gscToken) return

        if (isIntlLocale) {
            stripTokenFromLocation()
            tokenRef.current = null
            return
        }

        if (preferencesLoading || customerType == null || basketLoading) return

        if (!enabled) {
            stripTokenFromLocation()
            tokenRef.current = null
            return
        }

        if (basket?.c_gscHandoffTokenId === gscToken) {
            stripTokenFromLocation()
            tokenRef.current = null
            return
        }

        if (inFlightTokens.has(gscToken)) return
        inFlightTokens.add(gscToken)
        // Take the token out of the URL now, not when the handoff finishes, so a refresh
        // or back navigation mid-handoff can't start a second run that adds the items again.
        stripTokenFromLocation()

        const showNotice = (message) => {
            if (isMountedRef.current) {
                showCustomToast({
                    message,
                    variant: 'subtle',
                    styles: {role: 'status', 'aria-live': 'polite'}
                })
            }
        }

        const processHandoff = async () => {
            try {
                bodyRef.current = JSON.stringify({gscToken, channel: 'web', brand: brandId})
                const response = unwrapResponse(await resolveHandoff.mutateAsync({}))

                if (response?.c_status === 'expired') {
                    // Not a failure — the token was already consumed or outlived its window.
                    // Logged at info, not via logFailure, so a reused/stale test link doesn't
                    // read as a bug when it's expected behavior.
                    logInfo('token expired, no basket mutation attempted', {
                        existingBasketId: basket?.basketId || null
                    })
                    showNotice(
                        formatMessage({
                            id: 'gsc.handoff.expired',
                            defaultMessage:
                                'This cart link has expired. Your existing cart is still available.'
                        })
                    )
                    return
                }

                if (response?.c_status !== 'resolved' || response?.c_enabled !== true) {
                    logFailure(
                        'token resolution did not succeed',
                        new Error(
                            `status: ${response?.c_status || 'missing'}, enabled: ${response?.c_enabled === true}`
                        )
                    )
                    return
                }

                if (!response.c_attribution?.ambassadorId || !response.c_attribution?.referralSource) {
                    logFailure('resolved response has incomplete attribution')
                    return
                }

                logInfo('token resolved, proceeding to stamp basket', {
                    site: site?.id,
                    brandId,
                    itemCount: Array.isArray(response.c_items) ? response.c_items.length : 0,
                    existingBasketId: basket?.basketId || null
                })

                let activeBasket = basket
                if (!activeBasket?.basketId) {
                    try {
                        activeBasket = unwrapResponse(await createBasket({body: {}}))
                    } catch (error) {
                        logFailure(`basket creation failed (HTTP ${error?.response?.status || 'unknown'})`, error)
                        return
                    }
                }

                if (!activeBasket?.basketId) {
                    logFailure('basket creation returned no basketId')
                    return
                }

                // Stamp the basket with this token before adding anything, so any later run
                // for the same token (another tab, a remount) sees c_gscHandoffTokenId and
                // skips instead of adding the items a second time.
                let stampedBasket = await stampBasket(
                    activeBasket.basketId,
                    response.c_attribution,
                    gscToken
                )
                logInfo('stampBasket returned', {
                    basketId: activeBasket.basketId,
                    stampedAmbassadorIdPresent: !!stampedBasket?.c_gscAmbassadorId,
                    stampedReferralSourcePresent: !!stampedBasket?.c_gscReferralSource,
                    stampedHandoffTokenMatches: stampedBasket?.c_gscHandoffTokenId === gscToken
                })

                const items = Array.isArray(response.c_items) ? response.c_items : []
                const failedItems = []
                const validItems = items.filter(isValidHandoffItem)
                if (validItems.length !== items.length) {
                    items
                        .filter((item) => !isValidHandoffItem(item))
                        .forEach((item) => {
                            failedItems.push({
                                sku: typeof item?.sku === 'string' && item.sku ? item.sku : 'unknown',
                                qty: Number.isInteger(item?.qty) && item.qty > 0 ? item.qty : 1,
                                reason: `Malformed handoff item from GSC: ${JSON.stringify(item)}`
                            })
                            logFailure(`item malformed, skipped: ${JSON.stringify(item)}`)
                        })
                }
                if (validItems.length) {
                    const body = validItems.map(({sku, qty}) => ({productId: sku, quantity: qty}))
                    try {
                        await addItemToBasket({parameters: {basketId: activeBasket.basketId}, body})
                    } catch (bulkError) {
                        let itemsToRetry = body
                        if (!isDefinitiveRejection(bulkError)) {
                            // The bulk add may have been saved before the error (a timeout or
                            // a dropped connection). Only retry items whose quantity did not
                            // go up, so nothing is added twice. If the basket can't be re-read,
                            // fall back to retrying every item.
                            try {
                                const before = quantitiesByProduct(activeBasket)
                                const reread = await refetch()
                                const after = quantitiesByProduct(
                                    reread?.data?.baskets?.[0] || reread?.data
                                )
                                itemsToRetry = body.filter(
                                    (item) =>
                                        (after[item.productId] || 0) -
                                            (before[item.productId] || 0) <
                                        item.quantity
                                )
                            } catch (rereadError) {
                                logFailure('basket re-read after bulk add failed', rereadError)
                            }
                        }
                        for (const item of itemsToRetry) {
                            try {
                                await addItemToBasket({
                                    parameters: {basketId: activeBasket.basketId},
                                    body: [item]
                                })
                            } catch (itemError) {
                                const reason = await extractFailureReason(itemError)
                                failedItems.push({sku: item.productId, qty: item.quantity, reason})
                                logFailure(`item add failed for ${item.productId}`, itemError)
                            }
                        }
                    }
                }

                if (failedItems.length) {
                    stampedBasket = await stampBasket(
                        activeBasket.basketId,
                        response.c_attribution,
                        gscToken,
                        stampedBasket || undefined,
                        failedItems
                    )
                }
                const refreshed = await refetch()
                const latestBasket = refreshed?.data?.baskets?.[0] || refreshed?.data
                if (latestBasket?.basketId && latestBasket.basketId !== activeBasket.basketId) {
                    logInfo('basket id changed after stamping, restamping onto latest basket', {
                        stampedBasketId: activeBasket.basketId,
                        latestBasketId: latestBasket.basketId
                    })
                    const restampedBasket = await restampBasket(
                        stampedBasket || {
                            c_gscAmbassadorId: response.c_attribution?.ambassadorId,
                            c_gscReferralSource: response.c_attribution?.referralSource,
                            c_gscAttributedAt: response.c_attribution?.attributedAt,
                            c_gscHandoffTokenId: gscToken
                        },
                        latestBasket.basketId,
                        latestBasket
                    )
                    logInfo('restampBasket returned', {
                        basketId: latestBasket.basketId,
                        restampedAmbassadorIdPresent: !!restampedBasket?.c_gscAmbassadorId,
                        restampedReferralSourcePresent: !!restampedBasket?.c_gscReferralSource
                    })
                    await refetch()
                }

                if (failedItems.length) {
                    showNotice(
                        formatMessage(
                            {
                                id: 'gsc.handoff.partial_add',
                                defaultMessage: "We couldn't add these items: {items}."
                            },
                            {items: failedItems.map((item) => item.sku).join(', ')}
                        )
                    )
                }

                logInfo('handoff finished', {
                    finalBasketId: latestBasket?.basketId || activeBasket.basketId,
                    addedItemCount: validItems.length,
                    failedItemCount: failedItems.length
                })
            } catch (error) {
                logFailure('processing failed', error)
            } finally {
                stripTokenFromLocation()
                tokenRef.current = null
                inFlightTokens.delete(gscToken)
            }
        }

        processHandoff()
    }, [
        addItemToBasket,
        basket?.basketId,
        basket?.c_gscHandoffTokenId,
        basketLoading,
        brandId,
        createBasket,
        customerType,
        enabled,
        formatMessage,
        gscToken,
        isIntlLocale,
        preferencesLoading,
        refetch,
        resolveHandoff,
        restampBasket,
        showCustomToast,
        stampBasket
    ])
}

export {getPreferenceId, getTokenFromLocation, stripTokenFromLocation}
export default useGscHandoff
